import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSWRConfig } from 'swr';

import { useExampleRunsInfinite } from '@/hooks/useSwr';
import { ExampleSchemaWithRuns } from '@/types/schema';

const PAGE_SIZE = 20;

export const useExamples = ({
  datasetId,
  sessionIds,
  filtersToFetch,
  comparativeExperiment,
  datasetShareToken,
}: {
  datasetId?: string;
  sessionIds?: string[];
  filtersToFetch?: Record<string, string[]>;
  comparativeExperiment: string | null;
  datasetShareToken?: string;
}) => {
  const [searchParams] = useSearchParams();
  const sortBy = searchParams.get('sort_by');
  const sortOrder = searchParams.get('sort_order');

  const { cache } = useSWRConfig();
  const [valueToUpdateCache, setValueToUpdateCache] = useState<
    ExampleSchemaWithRuns[][] | null
  >(null);
  const [total, setTotal] = useState(0);
  const {
    data: examplePages,
    isLoading: areExamplesLoading,
    isValidating: areExamplesValidating,
    size: examplesSize,
    setSize: setExamplesSize,
    mutate: mutateInfiniteExamplesSwr,
  } = useExampleRunsInfinite(
    PAGE_SIZE,
    datasetId ?? null,
    sessionIds
      ? {
          session_ids: sessionIds,
          filters: filtersToFetch,
          sort_params:
            sortBy && sortOrder
              ? {
                  sort_by: sortBy,
                  sort_order: sortOrder,
                }
              : undefined,
          comparative_experiment_id: comparativeExperiment ?? undefined,
          preview: true,
        }
      : null,
    { datasetShareToken },
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      keepPreviousData: true,
      // if we have fetched the full output for a given example run, merge it into the fetched data here
      onSuccess: (newData, key) => {
        // Take the last page which actually contains examples, to make sure we get the right total
        setTotal(
          newData
            .slice()
            .reverse()
            .find((page) => page.length > 0)?.headers?.['x-pagination-total'] ??
            0
        );
        // Get the current cached data before it gets replaced
        const previousData = cache.get(key)?.data;
        if (!previousData || !newData) return;

        // Create a map of previous outputs for quick lookup
        const previousOutputsMap = new Map();
        previousData.forEach((page) => {
          page.forEach((example) => {
            example.runs?.forEach((run) => {
              if (run.outputs && Object.keys(run.outputs).length > 0) {
                previousOutputsMap.set(`${example.id}-${run.id}`, run.outputs);
              }
            });
          });
        });

        // Merge previous outputs into new data
        const mergedData = newData.map((page) =>
          page.map((example) => ({
            ...example,
            runs:
              example.runs?.map((run) => {
                const previousOutput = previousOutputsMap.get(
                  `${example.id}-${run.id}`
                );
                return previousOutput &&
                  (!run.outputs || Object.keys(run.outputs).length === 0)
                  ? { ...run, outputs: previousOutput }
                  : run;
              }) || [],
          }))
        );
        setValueToUpdateCache(mergedData);
      },
    }
  );

  // this is for the above onSuccess hook. It seems you can't directly set the cache in the onSuccess hook,
  // so we set a state variable and use a useEffect to set the cache
  useEffect(() => {
    if (valueToUpdateCache) {
      mutateInfiniteExamplesSwr(valueToUpdateCache, { revalidate: false });
      setValueToUpdateCache(null);
    }
  }, [valueToUpdateCache, mutateInfiniteExamplesSwr]);

  const examples = useMemo(
    () => examplePages?.flatMap((page) => page),
    [examplePages]
  );

  const hasMoreToLoad = examples ? examples.length < total : false;

  const isNextPageValidating =
    areExamplesValidating &&
    examplesSize > 1 &&
    examplePages?.length != examplesSize;

  return {
    examples,
    hasMoreToLoad,
    isNextPageValidating,
    areExamplesLoading,
    setExamplesSize,
    mutateInfiniteExamplesSwr,
  };
};
